import { ChevronDown } from '@atom-learning/icons'
import * as RadixSelect from '@radix-ui/react-select'
import * as React from 'react'

import { styled } from '~/styled'

import { Icon } from '../icon/Icon'
import { useSelectMenuContext } from './SelectMenuContext'

const Trigger = styled(RadixSelect.Trigger, {
  base: [
    'appearance-none',
    'rounded-md',
    'block',
    'w-full',
    'font-body',
    'font-normal',
    'leading-[1.4]',
    'text-grey-1000',
    'text-left',
    'relative',
    'inline-flex',
    'items-center',
    'border-none',
    'hover:cursor-pointer',
    'disabled:opacity-30',
    'disabled:cursor-not-allowed',
    'focus:outline-2',
    'focus:outline-blue-800',
    'focus:outline-offset-1',
    'focus:outline-solid',
    'focus:z-1'
  ],
  variants: {
    size: {
      sm: ['h-8', 'pl-2', 'pr-8', 'text-sm'],
      md: ['h-10', 'pl-3', 'pr-10', 'text-md'],
      lg: ['h-12', 'pl-3', 'pr-10', 'text-md']
    },
    theme: {
      white: ['bg-white'],
      grey: ['bg-grey-100']
    },
    state: {
      error: ['bg-danger-light', 'focus:outline-danger']
    }
  }
})

const TriggerIcon = styled(Icon, {
  base: [
    'absolute',
    'top-1/2',
    '-translate-y-1/2',
    'pointer-events-none',
    'text-grey-700'
  ],
  variants: {
    size: {
      sm: ['right-2', 'size-4'],
      md: ['right-3', 'size-5'],
      lg: ['right-3', 'size-5']
    }
  }
})

type SelectMenuTriggerProps = React.ComponentPropsWithRef<
  typeof RadixSelect.Trigger
> & {
  placeholder?: string
}

export const SelectMenuTrigger = React.forwardRef<
  HTMLButtonElement,
  SelectMenuTriggerProps
>(({ placeholder, children, ...props }, ref) => {
  const { size, theme, state } = useSelectMenuContext()

  return (
    <Trigger ref={ref} size={size} theme={theme} state={state} {...props}>
      <RadixSelect.Value placeholder={placeholder}>
        {children}
      </RadixSelect.Value>
      <TriggerIcon is={ChevronDown} size={size} />
    </Trigger>
  )
})

SelectMenuTrigger.displayName = 'SelectMenuTrigger'
